/**
 * Typed HTTP client with an ApiResult unwrap contract.
 *
 * Every call returns `{ ok: true, data }` or `{ ok: false, err }` — never the
 * raw payload and never a thrown network error. Callers unwrap before mapping
 * fields (mirrors the EdgeForge convention).
 */

export type ApiError = { code: string; message: string; status?: number };
export type ApiResult<T> = { ok: true; data: T } | { ok: false; err: ApiError };

const TOKEN_KEY = "obf_auth_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (e) {
    return { ok: false, err: { code: "network_error", message: String(e) } };
  }

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const envelope = (payload as { error?: ApiError })?.error;
    return {
      ok: false,
      err: {
        code: envelope?.code ?? "http_error",
        message: envelope?.message ?? res.statusText,
        status: res.status,
      },
    };
  }

  return { ok: true, data: payload as T };
}

export const get = <T>(path: string, signal?: AbortSignal) => request<T>("GET", path, undefined, signal);
export const post = <T>(path: string, body?: unknown, signal?: AbortSignal) => request<T>("POST", path, body, signal);
export const put = <T>(path: string, body?: unknown, signal?: AbortSignal) => request<T>("PUT", path, body, signal);
export const patch = <T>(path: string, body?: unknown, signal?: AbortSignal) => request<T>("PATCH", path, body, signal);
export const del = <T>(path: string, signal?: AbortSignal) => request<T>("DELETE", path, undefined, signal);
